import { getTenantStorageKey } from './tenantStorage';

const STORAGE_KEY = 'terra-proxy-consumed-object-update-ids';
const RESOLVED_FIELDS_STORAGE_KEY = 'terra-proxy-resolved-object-update-fields';
const MAX_CONSUMED_IDS = 500;

function readPersistedStorageValue(storageKey: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const tenantKey = getTenantStorageKey(storageKey);
  if (tenantKey) {
    const tenantRaw = localStorage.getItem(tenantKey);
    if (tenantRaw) {
      return tenantRaw;
    }
  }
  return localStorage.getItem(storageKey);
}

function writePersistedStorageValue({
  storageKey,
  value,
}: {
  storageKey: string;
  value: string;
}): void {
  if (typeof window === 'undefined') {
    return;
  }
  const tenantKey = getTenantStorageKey(storageKey);
  if (tenantKey) {
    localStorage.setItem(tenantKey, value);
    return;
  }
  console.warn(
    'No tenant id; persisting object-update state without tenant scope',
    new Error('Missing tenant id for object-update persistence'),
  );
  localStorage.setItem(storageKey, value);
}

function readConsumedIds(): Set<string> {
  try {
    const raw = readPersistedStorageValue(STORAGE_KEY);
    if (!raw) {
      return new Set();
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return new Set();
    }
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch (error: unknown) {
    console.warn(
      'Failed to read consumed object-update ids from storage; treating as empty',
      error instanceof Error ? error : new Error(String(error)),
    );
    return new Set();
  }
}

function writeConsumedIds(ids: Set<string>): void {
  const bounded =
    ids.size > MAX_CONSUMED_IDS
      ? new Set([...ids].slice(ids.size - MAX_CONSUMED_IDS))
      : ids;
  try {
    writePersistedStorageValue({
      storageKey: STORAGE_KEY,
      value: JSON.stringify([...bounded]),
    });
  } catch (error: unknown) {
    console.warn(
      'Failed to persist consumed object-update ids to storage',
      error instanceof Error ? error : new Error(String(error)),
    );
  }
}

function readObjectUpdateToolCallScopeId(updateId: string): string | null {
  const separatorIndex = updateId.indexOf(':');
  if (separatorIndex === -1) {
    return null;
  }
  const nextSeparatorIndex = updateId.indexOf(':', separatorIndex + 1);
  if (nextSeparatorIndex === -1) {
    return updateId;
  }
  return updateId.slice(0, nextSeparatorIndex);
}

function readResolvedFieldsMap(): Record<string, string[]> {
  try {
    const raw = readPersistedStorageValue(RESOLVED_FIELDS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([, fields]) =>
          Array.isArray(fields) &&
          fields.every((field) => typeof field === 'string'),
      ),
    ) as Record<string, string[]>;
  } catch (error: unknown) {
    console.warn(
      'Failed to read resolved object-update fields from storage; treating as empty',
      error instanceof Error ? error : new Error(String(error)),
    );
    return {};
  }
}

function writeResolvedFieldsMap(map: Record<string, string[]>): void {
  try {
    writePersistedStorageValue({
      storageKey: RESOLVED_FIELDS_STORAGE_KEY,
      value: JSON.stringify(map),
    });
  } catch (error: unknown) {
    console.warn(
      'Failed to persist resolved object-update fields to storage',
      error instanceof Error ? error : new Error(String(error)),
    );
  }
}

export function getResolvedObjectUpdateFields(
  sourceUpdateId: string,
): Set<string> {
  const resolvedFields = readResolvedFieldsMap()[sourceUpdateId] ?? [];
  return new Set(resolvedFields);
}

export function markObjectUpdateFieldsResolved({
  sourceUpdateId,
  fields,
}: {
  sourceUpdateId: string;
  fields: string[];
}): void {
  const uniqueFields = [...new Set(fields.filter((field) => field.length > 0))];
  if (!sourceUpdateId || uniqueFields.length === 0) {
    return;
  }

  const resolvedFieldsMap = readResolvedFieldsMap();
  const existingFields = new Set(resolvedFieldsMap[sourceUpdateId] ?? []);
  for (const field of uniqueFields) {
    existingFields.add(field);
  }
  resolvedFieldsMap[sourceUpdateId] = [...existingFields];
  writeResolvedFieldsMap(resolvedFieldsMap);
}

function clearResolvedObjectUpdateFields(sourceUpdateIds: string[]): void {
  const uniqueIds = [...new Set(sourceUpdateIds.filter((id) => id.length > 0))];
  if (uniqueIds.length === 0) {
    return;
  }

  const resolvedFieldsMap = readResolvedFieldsMap();
  let changed = false;
  for (const sourceUpdateId of uniqueIds) {
    if (resolvedFieldsMap[sourceUpdateId] !== undefined) {
      delete resolvedFieldsMap[sourceUpdateId];
      changed = true;
    }
  }
  if (changed) {
    writeResolvedFieldsMap(resolvedFieldsMap);
  }
}

function legacyConsumedMatchesStableId({
  stableId,
  consumedIds,
}: {
  stableId: string;
  consumedIds: Set<string>;
}): boolean {
  const segments = stableId.split(':');
  if (segments.length !== 3) {
    return false;
  }
  const [toolCallId, objectKind, objectId] = segments;
  for (const consumedId of consumedIds) {
    const consumedParts = consumedId.split(':');
    if (
      consumedParts.length === 4 &&
      consumedParts[1] === toolCallId &&
      consumedParts[2] === objectId
    ) {
      return true;
    }
    if (
      consumedParts.length === 2 &&
      consumedParts[1] === toolCallId &&
      (objectKind === 'CustomRole' || objectKind === 'Finding')
    ) {
      return true;
    }
  }
  return false;
}

export function isObjectUpdateConsumed(id: string): boolean {
  const consumedIds = readConsumedIds();
  if (consumedIds.has(id)) {
    return true;
  }
  if (legacyConsumedMatchesStableId({ stableId: id, consumedIds })) {
    return true;
  }
  const scopeId = readObjectUpdateToolCallScopeId(id);
  if (scopeId && scopeId !== id && consumedIds.has(scopeId)) {
    return true;
  }
  return false;
}

export function markObjectUpdatesConsumed(ids: string[]): void {
  const uniqueIds = new Set(ids.filter((id) => id.length > 0));
  if (uniqueIds.size === 0) {
    return;
  }
  const consumedIds = readConsumedIds();
  for (const id of uniqueIds) {
    consumedIds.add(id);
  }
  writeConsumedIds(consumedIds);
  clearResolvedObjectUpdateFields([...uniqueIds]);
}
